// src/pages/patientLogin/index.jsx
import React, { useEffect, useMemo, useState } from "react";

import {
  Box,
  Typography,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  Alert,
  Divider,
  IconButton,
  Paper,
  Grid,
  Container,
  MenuItem,
  Select,
} from "@mui/material";

import PhoneIcon from "@mui/icons-material/Phone";
import LockIcon from "@mui/icons-material/Lock";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SearchIcon from "@mui/icons-material/Search";
import LocalPharmacyIcon from "@mui/icons-material/LocalPharmacy";
import MenuIcon from "@mui/icons-material/Menu";
import PublicIcon from "@mui/icons-material/Public";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HotelIcon from "@mui/icons-material/Hotel";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import BiotechIcon from "@mui/icons-material/Biotech";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import VideoCallIcon from "@mui/icons-material/VideoCall";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MedicalServicesOutlinedIcon from "@mui/icons-material/MedicalServicesOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";

import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { generateAndSendOTP, DEV_PATIENT_OTP, isDevPatientOtp } from "../../services/otpService";
import { toast } from "react-toastify";

import { getDoctorsByOrganizationId, getDoctorsByDepartmentIdMultiple } from "../../services/doctorService";
import { getAllHospitalOrganizations } from "../../services/hospitalOrganizationService";
import { getAllDepartment } from "../../services/departmentService";
import { filterDepartmentsForOpdWorkflow } from "../../utils/subscriptionScopeUtils";
import PatientAddProfile from "../patientDashboard/PatientAddProfile";
import PatientPortalHeader from "../patientPortal/PatientPortalHeader";
import {
  clearPendingAppointment,
  readPendingAppointment,
} from "../../utils/patientPortalSession";

/* ==========================================================================
   HMS THEME - Medanta-inspired landing (white, blue, grey)
   ========================================================================== */

const BLUE = "#0B78D0";
const BLUE_DARK = "#0866B3";
const BLUE_LIGHT = "#EAF4FC";
const BLUE_LIGHTER = "#F5F7FA";

const TEXT_DARK = "#2F3A4A";
const TEXT_GREY = "#6B7280";
const BORDER = "#E5E9EF";
const PAGE_GUTTER = { px: { xs: 2, sm: 3, md: 6, lg: 10 } };
const HERO_GREY = "#F3F4F6";

const SEARCH_FIELD_SX = {
  flex: "1 1 200px",
  minWidth: { xs: "100%", sm: 200 },
  "& .MuiOutlinedInput-root": {
    height: 56,
    backgroundColor: "#fff",
  },
};

const TOPO_BG =
  "radial-gradient(ellipse 90% 50% at 50% -8%, rgba(11,120,208,0.08) 0%, transparent 58%)";

const collectDoctorDepartmentIds = (doctor) => {
  const ids = new Set();
  const add = (value) => {
    if (value == null || value === "") return;
    if (Array.isArray(value)) {
      value.forEach(add);
      return;
    }
    if (typeof value === "object") {
      add(
        value.id ??
          value.departmentId ??
          value.department_id ??
          value.departmentID
      );
      return;
    }
    String(value)
      .split(/[,|]/)
      .forEach((part) => {
        const trimmed = part.trim();
        if (trimmed) ids.add(trimmed);
      });
  };

  add(doctor?.departmentId);
  add(doctor?.department_id);
  add(doctor?.departmentID);
  add(doctor?.departmentIds);
  add(doctor?.department_ids);
  add(doctor?.departments);
  return [...ids];
};

const getHospitalOrganizationId = (organization) => {
  const candidates = [
    organization?.organizationId,
    organization?.organization_id,
    organization?.id,
  ];
  for (const value of candidates) {
    if (value == null || value === "") continue;
    const numericId = Number(value);
    if (Number.isFinite(numericId) && numericId > 0) {
      return numericId;
    }
  }
  return "";
};

const mergeDoctorsById = (doctorList = []) => {
  const map = new Map();
  doctorList.forEach((doctor) => {
    const organizationKey =
      doctor?.organizationId ?? doctor?.organization_id ?? "";
    const key = `${organizationKey}::${
      doctor?.id ??
      doctor?.userId ??
      doctor?.user_id ??
      `${doctor?.name}-${doctor?.mobile}`
    }`;
    const incomingIds = collectDoctorDepartmentIds(doctor);
    const existing = map.get(key);
    if (!existing) {
      map.set(key, {
        ...doctor,
        departmentIds: incomingIds,
      });
      return;
    }
    map.set(key, {
      ...existing,
      ...doctor,
      organizationId:
        doctor.organizationId ??
        doctor.organization_id ??
        existing.organizationId ??
        existing.organization_id,
      departmentIds: [
        ...new Set([...collectDoctorDepartmentIds(existing), ...incomingIds]),
      ],
    });
  });
  return Array.from(map.values());
};

const hmsModules = [
  {
    icon: <EventAvailableIcon />,
    label: "OPD",
    description: "Appointments, tokens, consultations and outpatient records in one queue.",
  },
  {
    icon: <HotelIcon />,
    label: "IPD",
    description: "Admissions, wards, beds, nursing notes and discharge for in-patients.",
  },
  {
    icon: <LocalPharmacyIcon />,
    label: "Pharmacy",
    description: "Issue medicines against prescriptions and track stock in real time.",
  },
  {
    icon: <Inventory2Icon />,
    label: "Inventory",
    description: "Medicine and consumable purchase, stock alerts and store management.",
  },
  {
    icon: <BiotechIcon />,
    label: "Pathology lab",
    description: "Sample collection, lab tests, results and printable patient reports.",
  },
  {
    icon: <ReceiptLongIcon />,
    label: "Billing",
    description: "OPD, IPD, pharmacy and lab invoices with GST-ready billing.",
  },
  {
    icon: <AssignmentIndIcon />,
    label: "Patient records",
    description: "Registration, history, prescriptions and visit timelines.",
  },
  {
    icon: <PeopleAltIcon />,
    label: "Staff & attendance",
    description: "Role-based access for doctors, front desk, pharmacy and lab teams.",
  },
];


const quickActions = [
  {
    label: "Find a Doctor",
    section: "doctor-section",
    icon: <PersonSearchOutlinedIcon />,
  },
  {
    label: "Book Appointment",
    section: "doctor-section",
    icon: <CalendarMonthOutlinedIcon />,
    highlighted: true,
  },
  {
    label: "Book a Virtual Consultation",
    section: "doctor-section",
    icon: <VideoCallIcon />,
  },
  {
    label: "Book a Test",
    section: "modules-section",
    icon: <BiotechIcon />,
  },

  // Temporarily hidden quick actions:
  // {
  //   label: "Get Health Checkup",
  //   section: "modules-section",
  //   icon: <MedicalServicesOutlinedIcon />,
  // },
  // {
  //   label: "Second Opinion",
  //   section: "about-section",
  //   icon: <PeopleAltIcon />,
  // },
  // {
  //   label: "Homecare",
  //   section: "modules-section",
  //   icon: <HomeOutlinedIcon />,
  // },
  // {
  //   label: "Medicine Delivery",
  //   section: "modules-section",
  //   icon: <LocalPharmacyIcon />,
  // },
];


/* ==========================================================================\n   PATIENT PORTAL LANGUAGE SUPPORT\n   ========================================================================== */

const LANGUAGE_STORAGE_KEY = "hms_patient_language";

const translations = {
  en: {
    "Modules": "Modules",
    "Doctors": "Doctors",
    "My Profile": "My Profile",
    "Edit Profile": "Edit Profile",
    "About": "About",
    "Dashboard": "Dashboard",
    "Sign In": "Sign In",
    "Secure patient access": "Secure patient access",
    "Sign in with OTP or PIN to manage appointments, prescriptions, lab reports and medical records. HMS never sells patient data.": "Sign in with OTP or PIN to manage appointments, prescriptions, lab reports and medical records. HMS never sells patient data.",
    "Learn more": "Learn more",
    "Sign in": "Sign in",
    "to access HMS Patient Portal": "to access HMS Patient Portal",
    "Select Country": "Select Country",
    "Login with OTP": "Login with OTP",
    "Login with PIN": "Login with PIN",
    "Registered Mobile": "Registered Mobile",
    "Enter your registered mobile number": "Enter your registered mobile number",
    "Send OTP": "Send OTP",
    "Enter OTP": "Enter OTP",
    "Enter OTP (test: {otp})": "Enter OTP (test: {otp})",
    "OTP sent to +91": "OTP sent to +91",
    "Resend OTP": "Resend OTP",
    "Verify & Login": "Verify & Login",
    "PIN / Password": "PIN / Password",
    "Login": "Login",
    "Hospital staff?": "Hospital staff?",
    "Your healthcare information is protected and secure.": "Your healthcare information is protected and secure.",
    "All Rights Reserved.": "All Rights Reserved.",
    "Book Appointment": "Book Appointment",
    "Book": "Book",
    "Find a Doctor": "Find a Doctor",
    "Get Health Checkup": "Get Health Checkup",
    "Second Opinion": "Second Opinion",
    "Book a Virtual Consultation": "Book a Virtual Consultation",
    "Homecare": "Homecare",
    "Book a Test": "Book a Test",
    "Medicine Delivery": "Medicine Delivery",
    "HMS modules": "HMS modules",
    "Everything your hospital needs in one suite": "Everything your hospital needs in one suite",
    "From outpatient tokens to inpatient wards, medicine inventory and pathology lab tests — clinical, pharmacy and billing teams work from one source of truth.": "From outpatient tokens to inpatient wards, medicine inventory and pathology lab tests — clinical, pharmacy and billing teams work from one source of truth.",
    "OPD": "OPD",
    "Pathology": "Pathology",
    "Admissions, wards, beds, nursing notes and discharge for in-patients.": "Admissions, wards, beds, nursing notes and discharge for in-patients.",
    "IPD": "IPD",
    "Appointments, tokens, consultations and outpatient records in one queue.": "Appointments, tokens, consultations and outpatient records in one queue.",
    "Issue medicines against prescriptions and track stock in real time.": "Issue medicines against prescriptions and track stock in real time.",
    "Pharmacy": "Pharmacy",
    "Medicine and consumable purchase, stock alerts and store management.": "Medicine and consumable purchase, stock alerts and store management.",
    "Inventory": "Inventory",
    "Sample collection, lab tests, results and printable patient reports.": "Sample collection, lab tests, results and printable patient reports.",
    "Pathology lab": "Pathology lab",
    "OPD, IPD, pharmacy and lab invoices with GST-ready billing.": "OPD, IPD, pharmacy and lab invoices with GST-ready billing.",
    "Billing": "Billing",
    "Registration, history, prescriptions and visit timelines.": "Registration, history, prescriptions and visit timelines.",
    "Patient records": "Patient records",
    "Role-based access for doctors, front desk, pharmacy and lab teams.": "Role-based access for doctors, front desk, pharmacy and lab teams.",
    "Staff & attendance": "Staff & attendance",
    "Find a Doctor": "Find a Doctor",
    "Search specialists across hospital organizations, then book through the patient portal.": "Search specialists across hospital organizations, then book through the patient portal.",
    "Search doctors and specialities": "Search doctors and specialities",
    "Search by hospital": "Search by hospital",
    "All hospitals": "All hospitals",
    "Department": "Department",
    "All departments": "All departments",
    "Available Doctor": "Available Doctor",
    "Select a hospital first": "Select a hospital first",
    "No doctor available for this department": "No doctor available for this department",
    "No matching doctors": "No matching doctors",
    "Select doctor": "Select doctor",
    "Search Doctor or Department": "Search Doctor or Department",
    "Departments": "Departments",
    "Doctors": "Doctors",
    "No results found": "No results found",
    "Search and select a doctor or speciality first.": "Search and select a doctor or speciality first.",
    "Selected doctor could not be found. Please search again.": "Selected doctor could not be found. Please search again.",
    "Built for hospitals. Guided by care.": "Built for hospitals. Guided by care.",
    "HMS covers the full clinical cycle: registration, appointments and token queues, OPD and IPD records, pharmacy inventory, pathology workflows, GST billing and staff attendance - so your teams work from one source of truth.": "HMS covers the full clinical cycle: registration, appointments and token queues, OPD and IPD records, pharmacy inventory, pathology workflows, GST billing and staff attendance - so your teams work from one source of truth.",
    "OPD + IPD": "OPD + IPD",
    "Outpatient visits and inpatient admissions in connected dashboards.": "Outpatient visits and inpatient admissions in connected dashboards.",
    "Purchase, stock alerts and medicine issue tied to prescriptions.": "Purchase, stock alerts and medicine issue tied to prescriptions.",
    "Laboratory": "Laboratory",
    "Sample collection, results and reports for OPD and IPD patients.": "Sample collection, results and reports for OPD and IPD patients.",
    "Privacy first": "Privacy first",
    "Role-based access for patients, doctors, staff and administrators.": "Role-based access for patients, doctors, staff and administrators.",
    "Unified": "Unified",
    "Hospital modules": "Hospital modules",
    "Secure": "Secure",
    "Patient records": "Patient records",
    "24/7": "24/7",
    "Portal access": "Portal access",
    "One login": "One login",
    "OTP or PIN": "OTP or PIN",
    "Ready to do your best care work?": "Ready to do your best care work?",
    "Search a doctor, pick a slot, then use Sign In in the header to complete your booking.": "Search a doctor, pick a slot, then use Sign In in the header to complete your booking.",
    "For Patients": "For Patients",
    "Specialities": "Specialities",
    "Care at HMS": "Care at HMS",
    "Hospital Management System - patient portal, clinical records, pharmacy, laboratory and billing in a single suite.": "Hospital Management System - patient portal, clinical records, pharmacy, laboratory and billing in a single suite.",
    "Appointments": "Appointments",
    "Laboratory": "Laboratory",
    "Book specialists, manage visits and keep prescriptions and lab reports in one patient portal.": "Book specialists, manage visits and keep prescriptions and lab reports in one patient portal.",
    "Hospital Management System": "Hospital Management System",
    "Patient-first care across OPD, IPD and diagnostics": "Patient-first care across OPD, IPD and diagnostics",
    "HMS brings hospital teams together so patients get coordinated care — from booking to pharmacy and lab — in one platform.": "HMS brings hospital teams together so patients get coordinated care — from booking to pharmacy and lab — in one platform.",
    "Enter a valid 10-digit mobile number.": "Enter a valid 10-digit mobile number.",
    "Enter the OTP received on your mobile.": "Enter the OTP received on your mobile.",
    "Enter your PIN / Password.": "Enter your PIN / Password.",
    "OTP ready. Enter {otp} to sign in (SMS is not configured).": "OTP ready. Enter {otp} to sign in (SMS is not configured).",
    "OTP sent. For testing you can also use {otp}.": "OTP sent. For testing you can also use {otp}.",
    "OTP verified. Please create your profile.": "OTP verified. Please create your profile.",
    "Logged in successfully!": "Logged in successfully!",
    "Login failed. Please check your credentials.": "Login failed. Please check your credentials.",
    "Failed to send OTP. Please try again.": "Failed to send OTP. Please try again.",
    "Invalid OTP. Please try again.": "Invalid OTP. Please try again.",
    "Select Language": "Select Language",
    "Logout": "Logout",
    "Edit Profile": "Edit Profile",
    "English": "English",
    "Hindi": "Hindi",
    "Marathi": "Marathi",
  },
  hi: {
    "Modules": "मॉड्यूल्स", "Doctors": "डॉक्टर", "My Profile": "मेरी प्रोफ़ाइल", "About": "हमारे बारे में", "Dashboard": "डैशबोर्ड", "Sign In": "साइन इन", "Logout": "लॉगआउट", "Edit Profile": "प्रोफ़ाइल संपादित करें",
    "Secure patient access": "सुरक्षित मरीज प्रवेश", "Sign in with OTP or PIN to manage appointments, prescriptions, lab reports and medical records. HMS never sells patient data.": "अपॉइंटमेंट, प्रिस्क्रिप्शन, लैब रिपोर्ट और मेडिकल रिकॉर्ड प्रबंधित करने के लिए OTP या PIN से साइन इन करें। HMS मरीजों का डेटा कभी नहीं बेचता।", "Learn more": "और जानें", "Sign in": "साइन इन", "to access HMS Patient Portal": "HMS पेशेंट पोर्टल का उपयोग करने के लिए", "Select Country": "देश चुनें", "Login with OTP": "OTP से लॉगिन", "Login with PIN": "PIN से लॉगिन", "Registered Mobile": "रजिस्टर्ड मोबाइल", "Enter your registered mobile number": "अपना रजिस्टर्ड मोबाइल नंबर दर्ज करें", "Send OTP": "OTP भेजें", "Enter OTP": "OTP दर्ज करें", "Enter OTP (test: {otp})": "OTP दर्ज करें (टेस्ट: {otp})", "OTP sent to +91": "+91 पर OTP भेजा गया", "Resend OTP": "OTP फिर भेजें", "Verify & Login": "सत्यापित करें और लॉगिन करें", "PIN / Password": "PIN / पासवर्ड", "Login": "लॉगिन", "Hospital staff?": "अस्पताल स्टाफ हैं?", "Your healthcare information is protected and secure.": "आपकी स्वास्थ्य जानकारी सुरक्षित और संरक्षित है।", "All Rights Reserved.": "सर्वाधिकार सुरक्षित।",
    "Book Appointment": "अपॉइंटमेंट बुक करें", "Book": "बुक करें", "Find a Doctor": "डॉक्टर खोजें", "Get Health Checkup": "हेल्थ चेकअप कराएं", "Second Opinion": "दूसरी राय", "Book a Virtual Consultation": "वर्चुअल परामर्श बुक करें", "Homecare": "होमकेयर", "Book a Test": "टेस्ट बुक करें", "Medicine Delivery": "दवा डिलीवरी",
    "HMS modules": "HMS मॉड्यूल्स", "Everything your hospital needs in one suite": "आपके अस्पताल के लिए आवश्यक सब कुछ एक ही प्लेटफ़ॉर्म पर", "From outpatient tokens to inpatient wards, medicine inventory and pathology lab tests — clinical, pharmacy and billing teams work from one source of truth.": "OPD टोकन से लेकर IPD वार्ड, दवा इन्वेंटरी और पैथोलॉजी लैब टेस्ट तक — क्लिनिकल, फार्मेसी और बिलिंग टीमें एक ही स्रोत से काम करती हैं।",
    "OPD": "OPD", "Pathology": "पैथोलॉजी", "Appointments, tokens, consultations and outpatient records in one queue.": "अपॉइंटमेंट, टोकन, परामर्श और आउटपेशेंट रिकॉर्ड एक ही कतार में।", "IPD": "IPD", "Admissions, wards, beds, nursing notes and discharge for in-patients.": "भर्ती, वार्ड, बेड, नर्सिंग नोट्स और डिस्चार्ज की सुविधा।", "Pharmacy": "फार्मेसी", "Issue medicines against prescriptions and track stock in real time.": "प्रिस्क्रिप्शन के अनुसार दवाएं जारी करें और स्टॉक को रियल टाइम में ट्रैक करें।", "Inventory": "इन्वेंटरी", "Medicine and consumable purchase, stock alerts and store management.": "दवाओं और कंज्यूमेबल की खरीद, स्टॉक अलर्ट और स्टोर प्रबंधन।", "Pathology lab": "पैथोलॉजी लैब", "Sample collection, lab tests, results and printable patient reports.": "सैंपल कलेक्शन, लैब टेस्ट, परिणाम और प्रिंटेबल मरीज रिपोर्ट।", "Billing": "बिलिंग", "OPD, IPD, pharmacy and lab invoices with GST-ready billing.": "OPD, IPD, फार्मेसी और लैब के लिए GST-अनुकूल बिलिंग।", "Patient records": "मरीज रिकॉर्ड", "Registration, history, prescriptions and visit timelines.": "रजिस्ट्रेशन, इतिहास, प्रिस्क्रिप्शन और विज़िट टाइमलाइन।", "Staff & attendance": "स्टाफ और उपस्थिति", "Role-based access for doctors, front desk, pharmacy and lab teams.": "डॉक्टर, फ्रंट डेस्क, फार्मेसी और लैब टीम के लिए भूमिका-आधारित एक्सेस।",
    "Search specialists across hospital organizations, then book through the patient portal.": "अस्पताल संगठनों में विशेषज्ञ खोजें और फिर पेशेंट पोर्टल से बुक करें।", "Search doctors and specialities": "डॉक्टर और विशेषज्ञताएं खोजें", "Search by hospital": "अस्पताल से खोजें", "All hospitals": "सभी अस्पताल", "Department": "विभाग", "All departments": "सभी विभाग", "Available Doctor": "उपलब्ध डॉक्टर", "Select a hospital first": "पहले अस्पताल चुनें", "No doctor available for this department": "इस विभाग के लिए कोई डॉक्टर उपलब्ध नहीं है", "No matching doctors": "कोई मिलान वाला डॉक्टर नहीं", "Select doctor": "डॉक्टर चुनें", "Search Doctor or Department": "डॉक्टर या विभाग खोजें", "Departments": "विभाग", "Doctors": "डॉक्टर", "No results found": "कोई परिणाम नहीं मिला", "Search and select a doctor or speciality first.": "पहले डॉक्टर या विशेषज्ञता खोजकर चुनें।", "Selected doctor could not be found. Please search again.": "चयनित डॉक्टर नहीं मिला। कृपया फिर से खोजें।",
    "Built for hospitals. Guided by care.": "अस्पतालों के लिए बनाया गया, देखभाल को ध्यान में रखकर।", "HMS covers the full clinical cycle: registration, appointments and token queues, OPD and IPD records, pharmacy inventory, pathology workflows, GST billing and staff attendance - so your teams work from one source of truth.": "HMS पूरे क्लिनिकल चक्र को कवर करता है: रजिस्ट्रेशन, अपॉइंटमेंट और टोकन कतारें, OPD और IPD रिकॉर्ड, फार्मेसी इन्वेंटरी, पैथोलॉजी वर्कफ़्लो, GST बिलिंग और स्टाफ उपस्थिति — ताकि आपकी टीमें एक ही स्रोत से काम करें।", "OPD + IPD": "OPD + IPD", "Outpatient visits and inpatient admissions in connected dashboards.": "कनेक्टेड डैशबोर्ड में आउटपेशेंट विज़िट और इनपेशेंट एडमिशन।", "Purchase, stock alerts and medicine issue tied to prescriptions.": "प्रिस्क्रिप्शन से जुड़ी खरीद, स्टॉक अलर्ट और दवा जारी करना।", "Laboratory": "लैबोरेटरी", "Sample collection, results and reports for OPD and IPD patients.": "OPD और IPD मरीजों के लिए सैंपल कलेक्शन, परिणाम और रिपोर्ट।", "Privacy first": "गोपनीयता पहले", "Role-based access for patients, doctors, staff and administrators.": "मरीजों, डॉक्टरों, स्टाफ और एडमिन के लिए भूमिका-आधारित एक्सेस।",
    "Unified": "एकीकृत", "Hospital modules": "अस्पताल मॉड्यूल्स", "Secure": "सुरक्षित", "Portal access": "पोर्टल एक्सेस", "24/7": "24/7", "One login": "एक लॉगिन", "OTP or PIN": "OTP या PIN", "Ready to do your best care work?": "बेहतर देखभाल के लिए तैयार हैं?", "Search a doctor, pick a slot, then use Sign In in the header to complete your booking.": "डॉक्टर खोजें, समय चुनें और बुकिंग पूरी करने के लिए हेडर में साइन इन करें।",
    "For Patients": "मरीजों के लिए", "Specialities": "विशेषज्ञताएं", "Care at HMS": "HMS में देखभाल", "Hospital Management System - patient portal, clinical records, pharmacy, laboratory and billing in a single suite.": "हॉस्पिटल मैनेजमेंट सिस्टम - पेशेंट पोर्टल, क्लिनिकल रिकॉर्ड, फार्मेसी, लैबोरेटरी और बिलिंग एक ही प्लेटफ़ॉर्म पर।", "Appointments": "अपॉइंटमेंट", "Book specialists, manage visits and keep prescriptions and lab reports in one patient portal.": "विशेषज्ञ बुक करें, विज़िट प्रबंधित करें और प्रिस्क्रिप्शन व लैब रिपोर्ट एक ही पेशेंट पोर्टल में रखें।", "Hospital Management System": "हॉस्पिटल मैनेजमेंट सिस्टम", "Patient-first care across OPD, IPD and diagnostics": "OPD, IPD और डायग्नोस्टिक्स में मरीज-केंद्रित देखभाल", "HMS brings hospital teams together so patients get coordinated care — from booking to pharmacy and lab — in one platform.": "HMS अस्पताल की टीमों को एक साथ लाता है ताकि मरीजों को बुकिंग से लेकर फार्मेसी और लैब तक समन्वित देखभाल एक ही प्लेटफ़ॉर्म पर मिले।",
    "Enter a valid 10-digit mobile number.": "मान्य 10 अंकों का मोबाइल नंबर दर्ज करें।",
    "Enter the OTP received on your mobile.": "अपने मोबाइल पर प्राप्त OTP दर्ज करें।", "Enter your PIN / Password.": "अपना PIN / पासवर्ड दर्ज करें।", "OTP ready. Enter {otp} to sign in (SMS is not configured).": "OTP तैयार है। साइन इन करने के लिए {otp} दर्ज करें (SMS कॉन्फ़िगर नहीं है)।", "OTP sent. For testing you can also use {otp}.": "OTP भेजा गया। टेस्टिंग के लिए आप {otp} भी इस्तेमाल कर सकते हैं।", "OTP verified. Please create your profile.": "OTP सत्यापित है। कृपया अपनी प्रोफ़ाइल बनाएं।", "Logged in successfully!": "सफलतापूर्वक लॉगिन हुआ!", "Login failed. Please check your credentials.": "लॉगिन विफल हुआ। कृपया अपनी जानकारी जांचें।", "Failed to send OTP. Please try again.": "OTP भेजने में विफल। कृपया फिर प्रयास करें।", "Invalid OTP. Please try again.": "अमान्य OTP। कृपया फिर प्रयास करें।", "Select Language": "भाषा चुनें", "English": "अंग्रेज़ी", "Hindi": "हिंदी", "Marathi": "मराठी",
  },
  mr: {
    "Modules": "मॉड्यूल्स", "Doctors": "डॉक्टर", "My Profile": "माझी प्रोफाइल", "About": "आमच्याबद्दल", "Dashboard": "डॅशबोर्ड", "Sign In": "साइन इन", "Logout": "लॉगआउट", "Edit Profile": "प्रोफाइल संपादित करा",
    "Secure patient access": "सुरक्षित रुग्ण प्रवेश", "Sign in with OTP or PIN to manage appointments, prescriptions, lab reports and medical records. HMS never sells patient data.": "अपॉइंटमेंट, प्रिस्क्रिप्शन, लॅब रिपोर्ट आणि वैद्यकीय नोंदी व्यवस्थापित करण्यासाठी OTP किंवा PIN ने साइन इन करा. HMS रुग्णांचा डेटा कधीही विकत नाही.", "Learn more": "अधिक जाणून घ्या", "Sign in": "साइन इन", "to access HMS Patient Portal": "HMS पेशंट पोर्टल वापरण्यासाठी", "Select Country": "देश निवडा", "Login with OTP": "OTP ने लॉगिन", "Login with PIN": "PIN ने लॉगिन", "Registered Mobile": "नोंदणीकृत मोबाइल", "Enter your registered mobile number": "आपला नोंदणीकृत मोबाइल नंबर प्रविष्ट करा", "Send OTP": "OTP पाठवा", "Enter OTP": "OTP प्रविष्ट करा", "Enter OTP (test: {otp})": "OTP प्रविष्ट करा (टेस्ट: {otp})", "OTP sent to +91": "+91 वर OTP पाठवला आहे", "Resend OTP": "OTP पुन्हा पाठवा", "Verify & Login": "पडताळा आणि लॉगिन करा", "PIN / Password": "PIN / पासवर्ड", "Login": "लॉगिन", "Hospital staff?": "हॉस्पिटल स्टाफ आहात?", "Your healthcare information is protected and secure.": "आपली आरोग्यविषयक माहिती सुरक्षित आणि संरक्षित आहे.", "All Rights Reserved.": "सर्व हक्क राखीव.",
    "Book Appointment": "अपॉइंटमेंट बुक करा", "Book": "बुक करा", "Find a Doctor": "डॉक्टर शोधा", "Get Health Checkup": "आरोग्य तपासणी करा", "Second Opinion": "दुसरे मत", "Book a Virtual Consultation": "व्हर्च्युअल सल्ला बुक करा", "Homecare": "होमकेअर", "Book a Test": "टेस्ट बुक करा", "Medicine Delivery": "औषध वितरण",
    "HMS modules": "HMS मॉड्यूल्स", "Everything your hospital needs in one suite": "आपल्या हॉस्पिटलसाठी आवश्यक सर्व सुविधा एका प्लॅटफॉर्मवर", "From outpatient tokens to inpatient wards, medicine inventory and pathology lab tests — clinical, pharmacy and billing teams work from one source of truth.": "OPD टोकनपासून IPD वॉर्ड, औषध इन्व्हेंटरी आणि पॅथॉलॉजी लॅब टेस्टपर्यंत — क्लिनिकल, फार्मसी आणि बिलिंग टीम्स एका विश्वासार्ह स्रोतावर काम करतात.",
    "OPD": "OPD", "Pathology": "पॅथॉलॉजी", "Appointments, tokens, consultations and outpatient records in one queue.": "अपॉइंटमेंट, टोकन, सल्लामसलत आणि बाह्यरुग्ण नोंदी एका ठिकाणी।", "IPD": "IPD", "Admissions, wards, beds, nursing notes and discharge for in-patients.": "रुग्ण दाखल करणे, वॉर्ड, बेड, नर्सिंग नोंदी आणि डिस्चार्ज व्यवस्थापन।", "Pharmacy": "फार्मसी", "Issue medicines against prescriptions and track stock in real time.": "प्रिस्क्रिप्शननुसार औषधे द्या आणि स्टॉक रिअल टाइममध्ये ट्रॅक करा।", "Inventory": "इन्व्हेंटरी", "Medicine and consumable purchase, stock alerts and store management.": "औषधे व कंझ्युमेबल खरेदी, स्टॉक अलर्ट आणि स्टोअर व्यवस्थापन।", "Pathology lab": "पॅथॉलॉजी लॅब", "Sample collection, lab tests, results and printable patient reports.": "सॅम्पल कलेक्शन, लॅब टेस्ट, निकाल आणि प्रिंटेबल रुग्ण रिपोर्ट।", "Billing": "बिलिंग", "OPD, IPD, pharmacy and lab invoices with GST-ready billing.": "OPD, IPD, फार्मसी आणि लॅबसाठी GST-सुसंगत बिलिंग।", "Patient records": "रुग्ण नोंदी", "Registration, history, prescriptions and visit timelines.": "नोंदणी, वैद्यकीय इतिहास, प्रिस्क्रिप्शन आणि भेटींची टाइमलाइन।", "Staff & attendance": "स्टाफ आणि उपस्थिती", "Role-based access for doctors, front desk, pharmacy and lab teams.": "डॉक्टर, फ्रंट डेस्क, फार्मसी आणि लॅब टीमसाठी भूमिका-आधारित प्रवेश।",
    "Search specialists across hospital organizations, then book through the patient portal.": "हॉस्पिटल संस्थांमध्ये तज्ज्ञ शोधा आणि पेशंट पोर्टलद्वारे बुक करा।", "Search doctors and specialities": "डॉक्टर आणि तज्ज्ञता शोधा", "Search by hospital": "हॉस्पिटलनुसार शोधा", "All hospitals": "सर्व हॉस्पिटल्स", "Department": "विभाग", "All departments": "सर्व विभाग", "Available Doctor": "उपलब्ध डॉक्टर", "Select a hospital first": "प्रथम हॉस्पिटल निवडा", "No doctor available for this department": "या विभागासाठी डॉक्टर उपलब्ध नाही", "No matching doctors": "जुळणारे डॉक्टर नाहीत", "Select doctor": "डॉक्टर निवडा", "Search Doctor or Department": "डॉक्टर किंवा विभाग शोधा", "Departments": "विभाग", "Doctors": "डॉक्टर", "No results found": "परिणाम सापडला नाही", "Search and select a doctor or speciality first.": "प्रथम डॉक्टर किंवा तज्ज्ञता शोधून निवडा।", "Selected doctor could not be found. Please search again.": "निवडलेला डॉक्टर सापडला नाही. कृपया पुन्हा शोधा।",
    "Built for hospitals. Guided by care.": "हॉस्पिटलसाठी तयार, काळजीला केंद्रस्थानी ठेवून.", "HMS covers the full clinical cycle: registration, appointments and token queues, OPD and IPD records, pharmacy inventory, pathology workflows, GST billing and staff attendance - so your teams work from one source of truth.": "HMS संपूर्ण क्लिनिकल चक्र कव्हर करते: नोंदणी, अपॉइंटमेंट आणि टोकन रांगा, OPD व IPD नोंदी, फार्मसी इन्व्हेंटरी, पॅथॉलॉजी वर्कफ्लो, GST बिलिंग आणि स्टाफ उपस्थिती — त्यामुळे तुमच्या टीम्स एका विश्वासार्ह स्रोतावर काम करतात.", "OPD + IPD": "OPD + IPD", "Outpatient visits and inpatient admissions in connected dashboards.": "कनेक्टेड डॅशबोर्डमध्ये बाह्यरुग्ण भेटी आणि आंतररुग्ण दाखल प्रक्रिया।", "Purchase, stock alerts and medicine issue tied to prescriptions.": "प्रिस्क्रिप्शनशी जोडलेली खरेदी, स्टॉक अलर्ट आणि औषध वितरण।", "Laboratory": "लॅबोरेटरी", "Sample collection, results and reports for OPD and IPD patients.": "OPD आणि IPD रुग्णांसाठी सॅम्पल कलेक्शन, निकाल आणि रिपोर्ट।", "Privacy first": "गोपनीयता प्रथम", "Role-based access for patients, doctors, staff and administrators.": "रुग्ण, डॉक्टर, स्टाफ आणि प्रशासकांसाठी भूमिका-आधारित प्रवेश।",
    "Unified": "एकत्रित", "Hospital modules": "हॉस्पिटल मॉड्यूल्स", "Secure": "सुरक्षित", "Patient records": "रुग्ण नोंदी", "24/7": "24/7", "Portal access": "पोर्टल प्रवेश", "One login": "एक लॉगिन", "OTP or PIN": "OTP किंवा PIN", "Ready to do your best care work?": "उत्तम आरोग्यसेवेसाठी तयार आहात?", "Search a doctor, pick a slot, then use Sign In in the header to complete your booking.": "डॉक्टर शोधा, वेळ निवडा आणि बुकिंग पूर्ण करण्यासाठी हेडरमधील साइन इन वापरा।",
    "For Patients": "रुग्णांसाठी", "Specialities": "तज्ज्ञता", "Care at HMS": "HMS मधील आरोग्यसेवा", "Hospital Management System - patient portal, clinical records, pharmacy, laboratory and billing in a single suite.": "हॉस्पिटल मॅनेजमेंट सिस्टम - पेशंट पोर्टल, क्लिनिकल नोंदी, फार्मसी, लॅबोरेटरी आणि बिलिंग एका प्लॅटफॉर्मवर.", "Appointments": "अपॉइंटमेंट", "Book specialists, manage visits and keep prescriptions and lab reports in one patient portal.": "तज्ज्ञ बुक करा, भेटी व्यवस्थापित करा आणि प्रिस्क्रिप्शन व लॅब रिपोर्ट एका पेशंट पोर्टलमध्ये ठेवा.", "Hospital Management System": "हॉस्पिटल मॅनेजमेंट सिस्टम", "Patient-first care across OPD, IPD and diagnostics": "OPD, IPD आणि डायग्नोस्टिक्समध्ये रुग्ण-केंद्रित सेवा", "HMS brings hospital teams together so patients get coordinated care — from booking to pharmacy and lab — in one platform.": "HMS हॉस्पिटलच्या टीम्सना एकत्र आणते, त्यामुळे रुग्णांना बुकिंगपासून फार्मसी आणि लॅबपर्यंत समन्वित सेवा एका प्लॅटफॉर्मवर मिळते.",
    "Enter a valid 10-digit mobile number.": "वैध 10 अंकी मोबाइल नंबर प्रविष्ट करा.",
    "Enter the OTP received on your mobile.": "आपल्या मोबाइलवर प्राप्त झालेला OTP प्रविष्ट करा.", "Enter your PIN / Password.": "आपला PIN / पासवर्ड प्रविष्ट करा.", "OTP ready. Enter {otp} to sign in (SMS is not configured).": "OTP तयार आहे. साइन इन करण्यासाठी {otp} प्रविष्ट करा (SMS कॉन्फिगर केलेले नाही).", "OTP sent. For testing you can also use {otp}.": "OTP पाठवला आहे. टेस्टिंगसाठी {otp} देखील वापरू शकता.", "OTP verified. Please create your profile.": "OTP पडताळले आहे. कृपया आपली प्रोफाइल तयार करा.", "Logged in successfully!": "यशस्वीपणे लॉगिन झाले!", "Login failed. Please check your credentials.": "लॉगिन अयशस्वी. कृपया आपली माहिती तपासा.", "Failed to send OTP. Please try again.": "OTP पाठवता आला नाही. कृपया पुन्हा प्रयत्न करा.", "Invalid OTP. Please try again.": "अवैध OTP. कृपया पुन्हा प्रयत्न करा.", "Select Language": "भाषा निवडा", "English": "इंग्रजी", "Hindi": "हिंदी", "Marathi": "मराठी",
  },
};

const translate = (language, key) => translations[language]?.[key] ?? translations.en[key] ?? key;

/* ==========================================================================
   LOGIN MODAL
   Rainbow-style two-column patient portal login
   ========================================================================== */

const COUNTRY_CODES = [
  { code: "+91", name: "India", flag: "🇮🇳" },
  { code: "+1", name: "United States", flag: "🇺🇸" },
  { code: "+44", name: "United Kingdom", flag: "🇬🇧" },
  { code: "+971", name: "United Arab Emirates", flag: "🇦🇪" },
  { code: "+65", name: "Singapore", flag: "🇸🇬" },
  { code: "+61", name: "Australia", flag: "🇦🇺" },
  { code: "+64", name: "New Zealand", flag: "🇳🇿" },
  { code: "+27", name: "South Africa", flag: "🇿🇦" },
  { code: "+49", name: "Germany", flag: "🇩🇪" },
  { code: "+33", name: "France", flag: "🇫🇷" },
  { code: "+81", name: "Japan", flag: "🇯🇵" },
  { code: "+86", name: "China", flag: "🇨🇳" },
  { code: "+82", name: "South Korea", flag: "🇰🇷" },
  { code: "+39", name: "Italy", flag: "🇮🇹" },
  { code: "+34", name: "Spain", flag: "🇪🇸" },
  { code: "+7", name: "Russia", flag: "🇷🇺" },
  { code: "+92", name: "Pakistan", flag: "🇵🇰" },
  { code: "+880", name: "Bangladesh", flag: "🇧🇩" },
  { code: "+94", name: "Sri Lanka", flag: "🇱🇰" },
  { code: "+977", name: "Nepal", flag: "🇳🇵" },
];

const LoginModal = ({ onClose, onLoginSuccess, onOtpVerified, language = "en" }) => {
  const t = (key) => translate(language, key);
  const { loginWithOTP: authLoginWithOTP, login } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState("otp");

  const [mobile, setMobile] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [pin, setPin] = useState("");
  const [otp, setOtp] = useState("");

  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loggingIn, setLoggingIn] = useState(false);

  const [error, setError] = useState("");
  const [showPin, setShowPin] = useState(false);

  const mobileValid = /^\d{10}$/.test(mobile);
  const fullMobile = `${countryCode}${mobile}`;
  // Preserve the existing backend format for India while including the
  // selected country code for all other countries.
  const authMobile = countryCode === "+91" ? mobile : fullMobile;

  const startCountdown = () => {
    setCountdown(30);

    const timer = setInterval(() => {
      setCountdown((current) => {
        if (current <= 1) {
          clearInterval(timer);
          return 0;
        }

        return current - 1;
      });
    }, 1000);
  };

  const handleSendOtp = async () => {
    setError("");

    if (!mobileValid) {
      setError(t("Enter a valid 10-digit mobile number."));
      return;
    }

    setSendingOtp(true);

    try {
      const result = await generateAndSendOTP(authMobile, { allowDevPatientOtp: true });

      setOtpSent(true);
      startCountdown();

      toast.success(
        result.usedDevFallback
          ? t("OTP ready. Enter {otp} to sign in (SMS is not configured).").replace("{otp}", DEV_PATIENT_OTP)
          : t("OTP sent. For testing you can also use {otp}.").replace("{otp}", DEV_PATIENT_OTP)
      );
    } catch (e) {
      setError(e.message || t("Failed to send OTP. Please try again."));
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");

    if (!otp || otp.length < 4) {
      setError(t("Enter the OTP received on your mobile."));
      return;
    }

    setLoggingIn(true);

    try {
      if (isDevPatientOtp(otp)) {
        toast.success(t("OTP verified. Please create your profile."));
        onOtpVerified?.(authMobile);
        return;
      }

      await authLoginWithOTP(authMobile, otp, null);

      toast.success(t("Logged in successfully!"));
      onLoginSuccess();
    } catch (e) {
      setError(e.message || t("Invalid OTP. Please try again."));
    } finally {
      setLoggingIn(false);
    }
  };

  const handlePinLogin = async () => {
    setError("");

    if (!mobileValid) {
      setError(t("Enter a valid 10-digit mobile number."));
      return;
    }

    if (!pin) {
      setError(t("Enter your PIN / Password."));
      return;
    }

    setLoggingIn(true);

    try {
      await login(authMobile, pin, null);

      toast.success(t("Logged in successfully!"));

      onLoginSuccess();
    } catch (e) {
      setError(e.message || t("Login failed. Please check your credentials."));
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: 1300,
        backgroundColor: "#ECECEC",
        backgroundImage: TOPO_BG,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 0.75, sm: 1.5 },
        overflow: "hidden",
        boxSizing: "border-box",
      }}
      onClick={onClose}
    >
      <Paper
        elevation={0}
        onClick={(e) => e.stopPropagation()}
        sx={{
          width: "100%",
          maxWidth: 980,
          height: { xs: "calc(100vh - 12px)", sm: "calc(100vh - 24px)", md: 560 },
          maxHeight: "calc(100vh - 12px)",
          borderRadius: "12px",
          overflow: "hidden",
          boxSizing: "border-box",
          boxShadow: "0 8px 40px rgba(0,0,0,0.12)",
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            zIndex: 10,
            color: TEXT_GREY,
            "&:hover": { backgroundColor: BLUE_LIGHT, color: TEXT_DARK },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Grid container sx={{ height: "100%", flexDirection: { xs: "column", md: "row-reverse" }, overflow: "hidden" }}>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: { xs: "none", md: "flex" },
              backgroundColor: "#F8FAFC",
              borderLeft: { md: `1px solid ${BORDER}` },
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: 0,
                p: { md: 5 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  backgroundColor: "#E8F1FB",
                  color: BLUE,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2.5,
                }}
              >
                <LockOutlinedIcon sx={{ fontSize: 34 }} />
              </Box>

              <Typography sx={{ fontSize: 22, fontWeight: 700, color: TEXT_DARK, mb: 1 }}>
                Secure patient access
              </Typography>

              <Typography
                sx={{
                  fontSize: 14,
                  color: TEXT_GREY,
                  lineHeight: 1.7,
                  maxWidth: 340,
                  mb: 2.5,
                }}
              >
                {t("Sign in with OTP or PIN to manage appointments, prescriptions, lab reports and medical records. HMS never sells patient data.")}
              </Typography>

              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  textTransform: "none",
                  borderColor: BLUE,
                  color: BLUE,
                  borderRadius: "20px",
                  px: 2.5,
                  fontWeight: 600,
                }}
              >
                Learn more
              </Button>

              <Box sx={{ display: "flex", gap: 0.8, mt: 4 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: BLUE }} />
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#D0D5DD" }} />
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#D0D5DD" }} />
              </Box>
            </Box>
          </Grid>

          {/* ================================================================
              RIGHT LOGIN FORM
              ================================================================ */}

          <Grid item xs={12} md={6} sx={{ minWidth: 0, minHeight: 0, overflow: "hidden" }}>
            <Box
              sx={{
                p: { xs: 2, sm: 3, md: 5 },
                height: "100%",
                minHeight: 0,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                backgroundColor: "#FFFFFF",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: { xs: 1.5, sm: 2 },
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    backgroundColor: BLUE,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LocalHospitalIcon sx={{ fontSize: 22 }} />
                </Box>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: TEXT_DARK }}>
                  HMS
                </Typography>
              </Box>

              <Typography sx={{ fontSize: { xs: 25, sm: 30 }, fontWeight: 700, color: TEXT_DARK, lineHeight: 1.15 }}>
                Sign in
              </Typography>
              <Typography sx={{ color: TEXT_GREY, fontSize: 14, mb: { xs: 1.5, sm: 2 }, mt: 0.5 }}>
                to access HMS Patient Portal
              </Typography>

              {/* LOGIN TYPE */}

              <Box
                sx={{
                  display: "flex",
                  backgroundColor: BLUE_LIGHT,
                  borderRadius: 1.8,
                  p: 0.5,
                  mb: { xs: 1.5, sm: 2 },
                }}
              >
                <Button
                  fullWidth
                  onClick={() => {
                    setTab("otp");
                    setError("");
                  }}
                  sx={{
                    borderRadius: 1.4,
                    backgroundColor:
                      tab === "otp" ? "#FFFFFF" : "transparent",
                    color: tab === "otp" ? BLUE_DARK : TEXT_GREY,
                    fontWeight: 700,
                    boxShadow: tab === "otp" ? 1 : 0,
                    "&:hover": {
                      backgroundColor: "#FFFFFF",
                    },
                  }}
                >
                  Login with OTP
                </Button>

                <Button
                  fullWidth
                  onClick={() => {
                    setTab("pin");
                    setError("");
                  }}
                  sx={{
                    borderRadius: 1.4,
                    backgroundColor:
                      tab === "pin" ? "#FFFFFF" : "transparent",
                    color: tab === "pin" ? BLUE_DARK : TEXT_GREY,
                    fontWeight: 700,
                    boxShadow: tab === "pin" ? 1 : 0,
                    "&:hover": {
                      backgroundColor: "#FFFFFF",
                    },
                  }}
                >
                  Login with PIN
                </Button>
              </Box>

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: { xs: 1.25, sm: 1.5 },
                    borderRadius: 1.5,
                  }}
                >
                  {error}
                </Alert>
              )}

              {/* REGISTERED MOBILE */}

              <Typography
                sx={{
                  fontWeight: 700,
                  color: TEXT_DARK,
                  fontSize: 14,
                  mb: 0.8,
                }}
              >
                Registered Mobile
              </Typography>

              <TextField
                fullWidth
                size="small"
                placeholder={t("Enter your registered mobile number")}
                value={mobile}
                onChange={(e) => {
                  setMobile(
                    e.target.value.replace(/\D/g, "").slice(0, 10)
                  );
                  setError("");
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ ml: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.35,
                          pr: 0.8,
                          borderRight: `1px solid ${BORDER}`,
                        }}
                      >
                        <PhoneIcon
                          sx={{
                            fontSize: 18,
                            color: BLUE,
                          }}
                        />
                        <Select
                          value={countryCode}
                          onChange={(e) => {
                            setCountryCode(e.target.value);
                            setOtpSent(false);
                            setOtp("");
                            setCountdown(0);
                            setError("");
                          }}
                          variant="standard"
                          disableUnderline
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 280,
                              },
                            },
                          }}
                          sx={{
                            minWidth: { xs: 76, sm: 86 },
                            fontSize: 13,
                            color: TEXT_GREY,
                            "& .MuiSelect-select": {
                              py: 0.4,
                              pr: "22px !important",
                              display: "flex",
                              alignItems: "center",
                            },
                          }}
                          renderValue={(value) => {
                            const country = COUNTRY_CODES.find((item) => item.code === value);
                            return country ? `${country.flag} ${country.code}` : value;
                          }}
                        >
                          {COUNTRY_CODES.map((country) => (
                            <MenuItem key={`${country.name}-${country.code}`} value={country.code}>
                              {country.flag} {country.name} ({country.code})
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.8,
                    minHeight: 46,
                  },
                  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                    borderColor: BLUE,
                    borderWidth: 1.5,
                  },
                }}
              />

              {/* ============================================================
                  OTP
                  ============================================================ */}

              {tab === "otp" && (
                <>
                  {!otpSent ? (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSendOtp}
                      disabled={sendingOtp || !mobileValid}
                      sx={{
                        minHeight: 48,
                        borderRadius: 1.8,
                        backgroundColor: BLUE,
                        fontSize: 15,
                        fontWeight: 700,
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: BLUE_DARK,
                          boxShadow: "none",
                        },
                      }}
                    >
                      {sendingOtp ? (
                        <CircularProgress
                          size={21}
                          color="inherit"
                        />
                      ) : (
                        "Send OTP"
                      )}
                    </Button>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        label={t("Enter OTP")}
                        placeholder={t("Enter OTP (test: {otp})").replace("{otp}", DEV_PATIENT_OTP)}
                        value={otp}
                        onChange={(e) =>
                          setOtp(
                            e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 6)
                          )
                        }
                        inputProps={{
                          inputMode: "numeric",
                        }}
                        sx={{
                          mb: 0.75,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.8,
                            minHeight: 46,
                          },
                          "& .MuiOutlinedInput-root.Mui-focused fieldset":
                            {
                              borderColor: BLUE,
                            },
                        }}
                      />

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            color: TEXT_GREY,
                          }}
                        >
                          {`${t("OTP sent to +91").replace("+91", countryCode)} ${mobile}`}
                        </Typography>

                        <Button
                          size="small"
                          disabled={countdown > 0}
                          onClick={handleSendOtp}
                          sx={{
                            color: BLUE_DARK,
                            p: 0,
                            minWidth: 0,
                            fontWeight: 700,
                          }}
                        >
                          {countdown > 0
                            ? `Resend ${countdown}s`
                            : t("Resend OTP")}
                        </Button>
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleVerifyOtp}
                        disabled={loggingIn}
                        sx={{
                          minHeight: 48,
                          borderRadius: 1.8,
                          backgroundColor: BLUE,
                          fontSize: 15,
                          fontWeight: 700,
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor: BLUE_DARK,
                            boxShadow: "none",
                          },
                        }}
                      >
                        {loggingIn ? (
                          <CircularProgress
                            size={21}
                            color="inherit"
                          />
                        ) : (
                          "Verify & Login"
                        )}
                      </Button>
                    </>
                  )}
                </>
              )}

              {/* ============================================================
                  PIN LOGIN
                  ============================================================ */}

              {tab === "pin" && (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("PIN / Password")}
                    type={showPin ? "text" : "password"}
                    value={pin}
                    onChange={(e) => {
                      setPin(e.target.value);
                      setError("");
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon
                            sx={{
                              fontSize: 18,
                              color: BLUE,
                            }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() =>
                              setShowPin(
                                (previous) => !previous
                              )
                            }
                          >
                            {showPin ? (
                              <VisibilityOffIcon fontSize="small" />
                            ) : (
                              <VisibilityIcon fontSize="small" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 1.5,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.8,
                        minHeight: 46,
                      },
                      "& .MuiOutlinedInput-root.Mui-focused fieldset":
                        {
                          borderColor: BLUE,
                        },
                    }}
                  />

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handlePinLogin}
                    disabled={loggingIn}
                    sx={{
                      minHeight: 48,
                      borderRadius: 1.8,
                      backgroundColor: BLUE,
                      fontSize: 15,
                      fontWeight: 700,
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: BLUE_DARK,
                        boxShadow: "none",
                      },
                    }}
                  >
                    {loggingIn ? (
                      <CircularProgress
                        size={21}
                        color="inherit"
                      />
                    ) : (
                      "Login"
                    )}
                  </Button>
                </>
              )}

              <Divider sx={{ my: { xs: 1.5, sm: 2 } }} />

              <Typography
                sx={{
                  textAlign: "center",
                  color: TEXT_GREY,
                  fontSize: 13,
                }}
              >
                Hospital staff?{" "}
                <Button
                  size="small"
                  onClick={() => {
                    onClose();
                    navigate("/login");
                  }}
                  sx={{
                    color: BLUE,
                    fontWeight: 700,
                    ml: 0.2,
                    p: 0,
                    minWidth: 0,
                    textTransform: "none",
                  }}
                >
                  Sign in
                </Button>
              </Typography>

              <Typography variant="body2" align="center" sx={{ marginTop: 0.5, color: "#5F6368" }}>
                Patient?{" "}
                <Button
                  variant="text"
                  onClick={onClose}
                  sx={{
                    textTransform: "none",
                    color: "#0B78D0",
                    padding: 0,
                    minWidth: "auto",
                    fontWeight: 700,
                    "&:hover": { backgroundColor: "transparent" },
                  }}
                >
                  Home Page
                </Button>
              </Typography>

              <Typography
                sx={{
                  mt: 1,
                  textAlign: "center",
                  fontSize: 11,
                  color: "#94A3B8",
                }}
              >
                {t("Your healthcare information is protected and secure.")}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      <Typography sx={{ mt: 1, fontSize: 11, color: "#8A8A8A" }}>
        © {new Date().getFullYear()} HMS. {t("All Rights Reserved.")}
      </Typography>
    </Box>
  );
};

/* ==========================================================================
   PATIENT HOME PAGE
   ========================================================================== */

const PatientLoginPage = () => {
  const [language, setLanguage] = useState(() => {
    try {
      return localStorage.getItem(LANGUAGE_STORAGE_KEY) || "en";
    } catch {
      return "en";
    }
  });

  const t = (key) => translate(language, key);

  const changeLanguage = (nextLanguage) => {
    setLanguage(nextLanguage);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
    } catch {
      // Ignore storage errors; the selected language still applies for this session.
    }
  };

  const [showModal, setShowModal] = useState(false);
  const [verifiedMobile, setVerifiedMobile] = useState("");

  const [pendingBookTest, setPendingBookTest] = useState(false);

  useEffect(() => {
    const pending = readPendingAppointment();
    if (pending && !pending.confirmOnReturn) {
      clearPendingAppointment();
    }
  }, []);

  const [hospitalOrganizations, setHospitalOrganizations] = useState([]);
  const [doctors, setDoctors] = useState([]);

  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedDoctor, setSelectedDoctor] = useState("");
  const [doctorSearch, setDoctorSearch] = useState("");

  const [loadingLocations, setLoadingLocations] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [departmentList, setDepartmentList] = useState([]);
  const [departmentDoctors, setDepartmentDoctors] = useState([]);

  useEffect(() => {
    let isMounted = true;
  
    const loadHospitalOrganizations = async () => {
      try {
        setLoadingLocations(true);
  
        const data = await getAllHospitalOrganizations();
  
        if (!isMounted) return;
  
        const organizations = Array.isArray(data) ? data : [];
  
        // Only show active hospital organizations when the field exists.
        const activeOrganizations = organizations.filter((org) => {
          const activeValue = org?.isActive ?? org?.is_active;
  
          return (
            activeValue === undefined ||
            activeValue === null ||
            activeValue === true ||
            activeValue === 1 ||
            activeValue === "1"
          );
        });
  
        setHospitalOrganizations(activeOrganizations);
      } catch (error) {
        console.error(
          "Error loading hospital organizations:",
          error
        );
  
        if (isMounted) {
          setHospitalOrganizations([]);
        }
      } finally {
        if (isMounted) {
          setLoadingLocations(false);
        }
      }
    };
  
    loadHospitalOrganizations();
  
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadDepartments = async () => {
      try {
        setLoadingDepartments(true);
        const data = await getAllDepartment();
        if (!isMounted) return;
        setDepartmentList(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error loading departments:", error);
        if (isMounted) {
          setDepartmentList([]);
        }
      } finally {
        if (isMounted) {
          setLoadingDepartments(false);
        }
      }
    };

    loadDepartments();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
  
    const loadDoctors = async () => {
      if (!hospitalOrganizations.length) {
        setDoctors([]);
        return;
      }
  
      try {
        setLoadingDoctors(true);
  
        const doctorResults = await Promise.all(
          hospitalOrganizations
            .map((organization) => {
              const organizationId = getHospitalOrganizationId(organization);
  
              if (!organizationId) {
                return null;
              }
  
              return getDoctorsByOrganizationId(organizationId)
                .then((organizationDoctors) =>
                  (Array.isArray(organizationDoctors)
                    ? organizationDoctors
                    : []
                  ).map((doctor) => ({
                    ...doctor,
                    organizationId: Number(organizationId),
                  }))
                )
                .catch((error) => {
                  console.warn(
                    `Unable to load doctors for organization ${organizationId}:`,
                    error
                  );
  
                  return [];
                });
            })
            .filter(Boolean)
        );
  
        if (!isMounted) return;
  
        const allDoctors = doctorResults.flat();
        setDoctors(mergeDoctorsById(allDoctors));
      } catch (error) {
        console.error("Error loading doctors:", error);
  
        if (isMounted) {
          setDoctors([]);
        }
      } finally {
        if (isMounted) {
          setLoadingDoctors(false);
        }
      }
    };
  
    loadDoctors();
  
    return () => {
      isMounted = false;
    };
  }, [hospitalOrganizations]);

  useEffect(() => {
    let isMounted = true;

    const loadDoctorsForDepartment = async () => {
      if (!selectedLocation || !selectedDepartment) {
        setDepartmentDoctors([]);
        return;
      }

      try {
        const list = await getDoctorsByDepartmentIdMultiple(selectedDepartment, {
          organizationId: selectedLocation,
        });
        if (!isMounted) return;
        const mapped = (Array.isArray(list) ? list : []).map((doctor) => ({
          ...doctor,
          organizationId: Number(selectedLocation),
          departmentIds: [
            ...new Set([
              ...collectDoctorDepartmentIds(doctor),
              String(selectedDepartment),
            ]),
          ],
        }));
        setDepartmentDoctors(mergeDoctorsById(mapped));
      } catch (error) {
        console.warn("Unable to load doctors for selected department:", error);
        if (isMounted) {
          setDepartmentDoctors([]);
        }
      }
    };

    loadDoctorsForDepartment();

    return () => {
      isMounted = false;
    };
  }, [selectedLocation, selectedDepartment]);

  const getOrganizationName = (organization) => {
    return (
      organization?.name ??
      organization?.hospitalName ??
      organization?.hospital_name ??
      organization?.organizationName ??
      organization?.organization_name ??
      "Hospital"
    );
  };

  const getDoctorId = (doctor) =>
    doctor?.id ?? doctor?.userId ?? doctor?.user_id;

  const getDoctorName = (doctor) =>
    doctor?.name ?? doctor?.fullName ?? doctor?.full_name ?? "Doctor";

  const getDoctorDepartmentName = (doctor) =>
    doctor?.departmentName ??
    doctor?.department_name ??
    doctor?.speciality ??
    doctor?.specialty ??
    doctor?.specialization ??
    "";

  const getDoctorDepartmentIds = (doctor) => collectDoctorDepartmentIds(doctor);

  const getDepartmentName = (department) =>
    department?.name ?? department?.departmentName ?? department?.department_name ?? "";

  const allDepartments = useMemo(() => {
    const opdDepartments = filterDepartmentsForOpdWorkflow(departmentList, {
      keepIds: [selectedDepartment],
    });
    return [...opdDepartments].sort((a, b) =>
      String(getDepartmentName(a)).localeCompare(String(getDepartmentName(b)))
    );
  }, [departmentList, selectedDepartment]);

  const doctorsInSelectedHospital = useMemo(() => {
    // Department API results are the source of truth for hospital + department.
    if (selectedLocation && selectedDepartment && departmentDoctors.length > 0) {
      return departmentDoctors;
    }

    // When no hospital is selected, keep all loaded doctors available so the
    // lower "Search Doctor or Department" field can search across hospitals.
    const combined = mergeDoctorsById([
      ...doctors,
      ...(selectedDepartment ? departmentDoctors : []),
    ]);

    if (!selectedLocation) return combined;

    return combined.filter((doctor) => {
      const doctorOrganizationId =
        doctor?.organizationId ?? doctor?.organization_id;
      if (
        doctorOrganizationId != null &&
        doctorOrganizationId !== "" &&
        Number(doctorOrganizationId) !== Number(selectedLocation)
      ) {
        return false;
      }
      return true;
    });
  }, [doctors, departmentDoctors, selectedLocation, selectedDepartment]);

  const searchValue = doctorSearch.trim().toLowerCase();

  const getDoctorDepartmentNames = (doctor) => {
    const names = new Set();

    const addName = (value) => {
      if (value == null || value === "") return;
      if (Array.isArray(value)) {
        value.forEach(addName);
        return;
      }
      if (typeof value === "object") {
        addName(
          value.name ??
            value.departmentName ??
            value.department_name ??
            value.speciality ??
            value.specialty ??
            value.specialization
        );
        return;
      }
      const normalized = String(value).trim();
      if (normalized) names.add(normalized);
    };

    addName(doctor?.departmentName);
    addName(doctor?.department_name);
    addName(doctor?.speciality);
    addName(doctor?.specialty);
    addName(doctor?.specialization);
    addName(doctor?.departmentNames);
    addName(doctor?.departments);

    // Some API responses contain only department IDs. Resolve those IDs
    // against the department master list so department text search still works.
    getDoctorDepartmentIds(doctor).forEach((departmentId) => {
      const department = allDepartments.find(
        (item) => String(item?.id) === String(departmentId)
      );
      if (department) addName(getDepartmentName(department));
    });

    return [...names];
  };

  const filteredDepartments = useMemo(() => {
    if (!searchValue) return allDepartments;

    return allDepartments.filter((department) => {
      const departmentName = String(getDepartmentName(department)).toLowerCase();

      if (departmentName.includes(searchValue)) return true;

      return doctorsInSelectedHospital.some((doctor) => {
        const doctorName = String(getDoctorName(doctor)).toLowerCase();
        if (!doctorName.includes(searchValue)) return false;

        const doctorIds = getDoctorDepartmentIds(doctor).map(String);
        if (doctorIds.includes(String(department.id))) return true;

        return getDoctorDepartmentNames(doctor).some(
          (name) => name.toLowerCase() === departmentName
        );
      });
    });
  }, [allDepartments, searchValue, doctorsInSelectedHospital]);

  const doctorMatchesDepartment = (doctor, departmentId) => {
    if (!departmentId) return true;

    const wanted = String(departmentId);
    const doctorIds = getDoctorDepartmentIds(doctor).map(String);

    if (doctorIds.includes(wanted)) return true;

    const selected = allDepartments.find(
      (department) => String(department.id) === wanted
    );

    if (!selected) return false;

    const deptName = String(getDepartmentName(selected)).trim().toLowerCase();
    if (!deptName) return false;

    return getDoctorDepartmentNames(doctor).some(
      (name) => name.trim().toLowerCase() === deptName
    );
  };

  const doctorMatchesSearch = (doctor) => {
    if (!searchValue) return true;

    const doctorName = String(getDoctorName(doctor)).toLowerCase();
    if (doctorName.includes(searchValue)) return true;

    return getDoctorDepartmentNames(doctor).some((departmentName) =>
      departmentName.toLowerCase().includes(searchValue)
    );
  };

  const filteredDoctors = useMemo(() => {
    return doctorsInSelectedHospital.filter((doctor) => {
      if (!doctorMatchesDepartment(doctor, selectedDepartment)) return false;
      return doctorMatchesSearch(doctor);
    });
  }, [
    doctorsInSelectedHospital,
    selectedDepartment,
    searchValue,
    allDepartments,
  ]);

  const searchHasValue = Boolean(searchValue);
  const searchHasResults =
    !searchHasValue || filteredDoctors.length > 0 || filteredDepartments.length > 0;

  useEffect(() => {
    if (
      selectedDepartment &&
      !filteredDepartments.some(
        (department) => String(department.id) === String(selectedDepartment)
      )
    ) {
      setSelectedDepartment("");
    }
  }, [selectedLocation, doctorSearch, filteredDepartments, selectedDepartment]);

  useEffect(() => {
    if (
      selectedDoctor &&
      !filteredDoctors.some(
        (doctor) => String(getDoctorId(doctor)) === String(selectedDoctor)
      )
    ) {
      setSelectedDoctor("");
    }
  }, [selectedLocation, selectedDepartment, doctorSearch, filteredDoctors, selectedDoctor]);

  const navigate = useNavigate();

  const { isAuthenticated, user, logout } = useAuth();

  const isPatient =
    user?.roleName?.toLowerCase() === "patient";

    const handleLoginSuccess = async () => {
      setShowModal(false);
    
      if (pendingBookTest) {
        setPendingBookTest(false);
        navigate("/patient?bookTest=1");
        return;
      }
    
      navigate("/patient");
    };

  const openPatientLogin = () => {
    if (isAuthenticated && isPatient) {
      navigate("/patient");
      return;
    }
    setShowModal(true);
  };

  const handleOtpVerified = (verifiedNumber) => {
    setShowModal(false);
    setVerifiedMobile(verifiedNumber);
  };

  const handleBookFromDoctorSearch = () => {
    if (!selectedDoctor) {
      toast.info(t("Search and select a doctor or speciality first."));
      handleNavigation("doctor-section");
      return;
    }

    const allAvailableDoctors = mergeDoctorsById([
      ...doctors,
      ...departmentDoctors,
    ]);

    const doctor = allAvailableDoctors.find((item) => {
      const doctorId = getDoctorId(item);
      return String(doctorId) === String(selectedDoctor);
    });

    if (!doctor) {
      toast.error(t("Selected doctor could not be found. Please search again."));
      return;
    }

    const organizationId =
      selectedLocation ||
      doctor?.organizationId ||
      doctor?.organization_id ||
      "";

    const query = organizationId
      ? `?org=${encodeURIComponent(organizationId)}`
      : "";

    navigate(`/patient/doctor/${selectedDoctor}${query}`);
  };

  const handleNavigation = (section) => {
    const element = document.getElementById(section);

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const handleOpenBookTest = () => {
    if (!isAuthenticated || !isPatient) {
      setPendingBookTest(true);
      setShowModal(true);
      return;
    }
    navigate("/patient?bookTest=1");
  };

  // When an authenticated patient switches from the dashboard to a landing-page tab,
  // honor the section hash after the landing page mounts.
  useEffect(() => {
    const section = window.location.hash.replace(/^#/, "");
    if (!section) return;

    const scrollToSection = () => {
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };

    const timer = window.setTimeout(scrollToSection, 0);
    return () => window.clearTimeout(timer);
  }, []);

  const patientName = user?.fullName || user?.name || user?.username || "Patient";

  const handleLandingLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const handleLandingEditProfile = () => {
    if (isAuthenticated && isPatient) {
      navigate("/patient?editProfile=1");
      return;
    }
    setShowModal(true);
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#FFFFFF", color: TEXT_DARK }}>
      <PatientPortalHeader
        language={language}
        t={t}
        isAuthenticated={isAuthenticated && isPatient}
        onLanguageChange={changeLanguage}
        onSignIn={openPatientLogin}
        onLogout={handleLandingLogout}
        onEditProfile={handleLandingEditProfile}
        onMyProfile={() => {
          if (isAuthenticated && isPatient) {
            navigate("/patient");
          } else {
            openPatientLogin();
          }
        }}
      />

      <Box
        sx={{
          position: "relative",
          overflow: "visible",
          backgroundColor: HERO_GREY,
          pt: { xs: 3.5, md: 5 },
          pb: { xs: 3, md: 4 },
        }}
      >
        <Container maxWidth={false} sx={PAGE_GUTTER}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              gap: { xs: 2.5, md: 5 },
            }}
          >
            <Box
              component="img"
              src="/assets/doct_login.svg"
              alt="HMS doctor"
              sx={{
                width: "100%",
                maxWidth: { xs: 300, md: 420 },
                height: { xs: 230, md: 340 },
                objectFit: "contain",
                objectPosition: "left center",
                flexShrink: 0,
              }}
            />
            <Box sx={{ flex: 1, minWidth: 0, maxWidth: 720 }}>
              <Typography
                sx={{
                  fontSize: { xs: 20, sm: 24, md: 28 },
                  fontWeight: 500,
                  lineHeight: 1.45,
                  color: TEXT_DARK,
                  mb: 1,
                }}
              >
                {t("HMS brings hospital teams together so patients get coordinated care — from booking to pharmacy and lab — in one platform.")}”
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: 15, color: TEXT_DARK }}>
                {t("Hospital Management System")}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: TEXT_GREY }}>
                {t("Patient-first care across OPD, IPD and diagnostics")}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: "#fff", pt: { xs: 2.5, md: 3.5 }, pb: { xs: 1.5, md: 2 } }}>
        <Container maxWidth={false} sx={PAGE_GUTTER}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(4, minmax(0, 1fr))",
              },
              gap: { xs: 1.25, md: 1.5 },
              width: "100%",
              alignItems: "stretch",
            }}
          >
            {quickActions.map((action) => (
              <Paper
                key={t(action.label)}
                elevation={action.highlighted ? 3 : 0}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (action.label === "Book a Test") {
                    handleOpenBookTest();
                    return;
                  }
                
                  handleNavigation(action.section);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                
                    if (action.label === "Book a Test") {
                      handleOpenBookTest();
                      return;
                    }
                
                    handleNavigation(action.section);
                  }
                }}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 1.25,
                  minWidth: 0,
                  minHeight: { xs: 56, sm: 60 },
                  height: "100%",
                  boxSizing: "border-box",
                  px: { xs: 1.25, sm: 1.5 },
                  py: 1,
                  cursor: "pointer",
                  borderRadius: "12px",
                  backgroundColor: action.highlighted ? BLUE : HERO_GREY,
                  color: action.highlighted ? "#FFFFFF" : TEXT_DARK,
                  border: action.highlighted
                    ? `2px solid ${BLUE_DARK}`
                    : `1px solid ${BORDER}`,
                  boxShadow: action.highlighted
                    ? "0 6px 18px rgba(11,120,208,0.22)"
                    : "none",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: action.highlighted ? BLUE_DARK : "#FFFFFF",
                    borderColor: BLUE,
                    boxShadow: "0 6px 18px rgba(11,120,208,0.14)",
                    transform: "translateY(-1px)",
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${BLUE}`,
                    outlineOffset: 3,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                    borderRadius: "50%",
                    border: action.highlighted
                      ? "1.5px solid rgba(255,255,255,0.8)"
                      : `1.5px dashed ${BLUE}`,
                    color: BLUE,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#FFFFFF",
                  }}
                >
                  {React.cloneElement(action.icon, {
                    sx: { fontSize: 18 },
                  })}
                </Box>

                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 700,
                    fontSize: { xs: 12, sm: 13.5 },
                    color: action.highlighted ? "#FFFFFF" : TEXT_DARK,
                    lineHeight: 1.3,
                    overflowWrap: "anywhere",
                  }}
                >
                  {t(action.label)}
                </Typography>

                <ArrowForwardIcon
                  sx={{
                    flexShrink: 0,
                    fontSize: 16,
                    color: action.highlighted ? "#FFFFFF" : BLUE,
                    transform: "rotate(-45deg)",
                  }}
                />
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      <Box id="modules-section" sx={{ py: { xs: 4, md: 5.5 }, backgroundColor: "#FFFFFF", scrollMarginTop: 80 }}>
        <Container maxWidth={false} sx={PAGE_GUTTER}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: BLUE, mb: 1 }}>
            {t("HMS modules")}
          </Typography>
          <Typography sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 800, mb: 1, color: TEXT_DARK }}>
            {t("Everything your hospital needs in one suite")}
          </Typography>
          <Typography sx={{ color: TEXT_GREY, maxWidth: 680, mb: 3, fontSize: 15, lineHeight: 1.65 }}>
            {t("From outpatient tokens to inpatient wards, medicine inventory and pathology lab tests — clinical, pharmacy and billing teams work from one source of truth.")}
          </Typography>
          <Grid container spacing={2.5}>
            {hmsModules.map((module) => (
              <Grid item xs={12} sm={6} md={3} key={t(module.label)}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    height: "100%",
                    borderRadius: "12px",
                    border: `1px solid ${BORDER}`,
                    "&:hover": { boxShadow: "0 10px 28px rgba(15,23,42,0.06)", borderColor: BLUE },
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: "10px",
                      backgroundColor: BLUE_LIGHT,
                      color: BLUE,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 1.5,
                    }}
                  >
                    {React.cloneElement(module.icon, { sx: { fontSize: 26 } })}
                  </Box>
                  <Typography sx={{ fontWeight: 800, mb: 0.6, color: TEXT_DARK }}>{t(module.label)}</Typography>
                  <Typography sx={{ fontSize: 13, color: TEXT_GREY, lineHeight: 1.6 }}>{t(module.description)}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box id="doctor-section" sx={{ py: { xs: 4, md: 5.5 }, backgroundColor: BLUE_LIGHTER, scrollMarginTop: 80 }}>
        <Container maxWidth={false} sx={PAGE_GUTTER}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.8px",
              textTransform: "uppercase",
              color: BLUE,
              mb: 1,
            }}
          >
            {t("Find a Doctor")}
          </Typography>
          <Typography sx={{ fontSize: { xs: 28, md: 34 }, fontWeight: 800, mb: 1, color: TEXT_DARK }}>
            {t("Search doctors and specialities")}
          </Typography>
          <Typography sx={{ color: TEXT_GREY, mb: 2, maxWidth: 560 }}>
            {t("Search specialists across hospital organizations, then book through the patient portal.")}
          </Typography>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: "12px",
              border: `1px solid ${BORDER}`,
              backgroundColor: "#fff",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              <TextField
                select
                label={t("Search by hospital")}
                value={selectedLocation}
                onChange={(e) => {
                  setSelectedLocation(e.target.value);
                  setSelectedDepartment("");
                  setSelectedDoctor("");
                }}
                disabled={loadingLocations}
                sx={SEARCH_FIELD_SX}
              >
                <MenuItem value="">{t("All hospitals")}</MenuItem>
                {hospitalOrganizations.map((organization) => {
                  const organizationId = getHospitalOrganizationId(organization);
                  if (!organizationId) return null;
                  return (
                    <MenuItem key={organizationId} value={organizationId}>
                      {getOrganizationName(organization)}
                    </MenuItem>
                  );
                })}
              </TextField>
              <TextField
                select
                label={t("Department")}
                value={selectedDepartment}
                onChange={(e) => {
                  setSelectedDepartment(e.target.value);
                  setSelectedDoctor("");
                }}
                disabled={!selectedLocation || loadingDepartments}
                sx={SEARCH_FIELD_SX}
              >
                <MenuItem value="">{t("All departments")}</MenuItem>
                {filteredDepartments.map((department) => (
                  <MenuItem key={department.id} value={department.id}>
                    {getDepartmentName(department)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t("Available Doctor")}
                value={selectedDoctor}
                onChange={(e) => setSelectedDoctor(e.target.value)}
                disabled={!selectedLocation || loadingDoctors}
                sx={SEARCH_FIELD_SX}
              >
                <MenuItem value="">
                  {!selectedLocation
                    ? t("Select a hospital first")
                    : selectedDepartment && filteredDoctors.length === 0
                      ? t("No doctor available for this department")
                      : filteredDoctors.length === 0
                        ? t("No matching doctors")
                        : t("Select doctor")}
                </MenuItem>
                {filteredDoctors.map((doctor) => {
                  const doctorId = getDoctorId(doctor);
                  return (
                    <MenuItem key={doctorId} value={doctorId}>
                      {getDoctorName(doctor)}
                    </MenuItem>
                  );
                })}
              </TextField>
              <Box
                sx={{
                  ...SEARCH_FIELD_SX,
                  flex: "1 1 240px",
                  position: "relative",
                }}
              >
                <TextField
                  fullWidth
                  label={t("Search Doctor or Department")}
                  value={doctorSearch}
                  onChange={(e) => {
                    setDoctorSearch(e.target.value);
                    setSelectedDoctor("");
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: TEXT_GREY }} />
                      </InputAdornment>
                    ),
                  }}
                />

                {searchHasValue && (
                  <Paper
                    elevation={4}
                    sx={{
                      position: "absolute",
                      top: 62,
                      left: 0,
                      right: 0,
                      zIndex: 20,
                      maxHeight: 320,
                      overflowY: "auto",
                      border: `1px solid ${BORDER}`,
                      borderRadius: "10px",
                      backgroundColor: "#fff",
                    }}
                  >
                    {!searchHasResults ? (
                      <Typography
                        sx={{
                          px: 2,
                          py: 2,
                          fontSize: 14,
                          color: TEXT_GREY,
                          textAlign: "center",
                        }}
                      >
                        {t("No results found")}
                      </Typography>
                    ) : (
                      <>
                        {filteredDepartments.length > 0 && (
                          <Box sx={{ p: 1 }}>
                            <Typography
                              sx={{
                                px: 1,
                                py: 0.6,
                                fontSize: 11,
                                fontWeight: 800,
                                color: TEXT_GREY,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              {t("Departments")}
                            </Typography>
                            {filteredDepartments.slice(0, 8).map((department) => (
                              <Box
                                key={`search-department-${department.id}`}
                                onClick={() => {
                                  setSelectedDepartment(String(department.id));
                                  setSelectedDoctor("");
                                  setDoctorSearch(getDepartmentName(department));
                                }}
                                sx={{
                                  px: 1,
                                  py: 1,
                                  borderRadius: "7px",
                                  cursor: "pointer",
                                  "&:hover": { backgroundColor: BLUE_LIGHT },
                                }}
                              >
                                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                  {getDepartmentName(department)}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}

                        {filteredDoctors.length > 0 && (
                          <Box sx={{ p: 1, borderTop: filteredDepartments.length ? `1px solid ${BORDER}` : "none" }}>
                            <Typography
                              sx={{
                                px: 1,
                                py: 0.6,
                                fontSize: 11,
                                fontWeight: 800,
                                color: TEXT_GREY,
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                              }}
                            >
                              {t("Doctors")}
                            </Typography>
                            {filteredDoctors.slice(0, 10).map((doctor) => {
                              const doctorId = getDoctorId(doctor);
                              const organizationId =
                                doctor?.organizationId ?? doctor?.organization_id ?? "";
                              const departmentNames = getDoctorDepartmentNames(doctor);

                              return (
                                <Box
                                  key={`search-doctor-${doctorId}`}
                                  onClick={() => {
                                    setSelectedDoctor(String(doctorId));
                                    if (organizationId) {
                                      setSelectedLocation(String(organizationId));
                                    }
                                    setDoctorSearch(getDoctorName(doctor));
                                  }}
                                  sx={{
                                    px: 1,
                                    py: 1,
                                    borderRadius: "7px",
                                    cursor: "pointer",
                                    "&:hover": { backgroundColor: BLUE_LIGHT },
                                  }}
                                >
                                  <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                                    {getDoctorName(doctor)}
                                  </Typography>
                                  {departmentNames.length > 0 && (
                                    <Typography sx={{ fontSize: 11.5, color: TEXT_GREY }}>
                                      {departmentNames.join(", ")}
                                    </Typography>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        )}
                      </>
                    )}
                  </Paper>
                )}
              </Box>
              <Button
                variant="contained"
                onClick={handleBookFromDoctorSearch}
                sx={{
                  height: 56,
                  minHeight: 56,
                  px: 3,
                  flex: { xs: "1 1 100%", sm: "0 0 120px" },
                  backgroundColor: BLUE,
                  boxShadow: "none",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { backgroundColor: BLUE_DARK, boxShadow: "none" },
                }}
              >
                {t("Book")}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>

      <Box id="about-section" sx={{ py: { xs: 4, md: 5.5 }, backgroundColor: "#fff", scrollMarginTop: 80 }}>
        <Container maxWidth={false} sx={PAGE_GUTTER}>
          <Typography sx={{ fontSize: { xs: 28, md: 34 }, fontWeight: 800, mb: 1.5 }}>
            {t("Built for hospitals. Guided by care.")}
          </Typography>
          <Typography sx={{ color: TEXT_GREY, maxWidth: 720, mb: 3, lineHeight: 1.75 }}>
            {t("HMS covers the full clinical cycle: registration, appointments and token queues, OPD and IPD records, pharmacy inventory, pathology workflows, GST billing and staff attendance - so your teams work from one source of truth.")}
          </Typography>
          <Grid container spacing={2}>
            {[
              ["OPD + IPD", "Outpatient visits and inpatient admissions in connected dashboards."],
              ["Pharmacy", "Purchase, stock alerts and medicine issue tied to prescriptions."],
              ["Laboratory", "Sample collection, results and reports for OPD and IPD patients."],
              ["Privacy first", "Role-based access for patients, doctors, staff and administrators."],
            ].map(([title, copy]) => (
              <Grid item xs={12} sm={6} md={3} key={title}>
                <Box sx={{ pr: 1 }}>
                  <Typography sx={{ fontWeight: 800, mb: 0.8 }}>{t(title)}</Typography>
                  <Typography sx={{ fontSize: 13.5, color: TEXT_GREY, lineHeight: 1.6 }}>{t(copy)}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 3, md: 3.5 }, backgroundColor: HERO_GREY, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}>
        <Container maxWidth={false} sx={PAGE_GUTTER}>
          <Grid container spacing={2}>
            {[
              ["Unified", "Hospital modules"],
              ["Secure", "Patient records"],
              ["24/7", "Portal access"],
              ["One login", "OTP or PIN"],
            ].map(([value, label]) => (
              <Grid item xs={6} md={3} key={label}>
                <Box sx={{ textAlign: "center", py: 1 }}>
                  <Typography sx={{ fontSize: { xs: 22, md: 28 }, fontWeight: 800, color: BLUE }}>{t(value)}</Typography>
                  <Typography sx={{ fontSize: 13, color: TEXT_GREY }}>{t(label)}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {!isAuthenticated && (
        <Box id="contact-section" sx={{ py: { xs: 4, md: 5.5 }, backgroundColor: "#FFFFFF" }}>
          <Container maxWidth={false} sx={{ ...PAGE_GUTTER, textAlign: "center" }}>
            <Box sx={{ maxWidth: 560, mx: "auto" }}>
            <Typography sx={{ fontSize: { xs: 26, md: 32 }, fontWeight: 800, mb: 1.5 }}>
              {t("Ready to do your best care work?")}
            </Typography>
            <Typography sx={{ color: TEXT_GREY, mb: 3, lineHeight: 1.7 }}>
              {t("Search a doctor, pick a slot, then use Sign In in the header to complete your booking.")}
            </Typography>
            <Button
              variant="contained"
              onClick={() => handleNavigation("doctor-section")}
              sx={{
                backgroundColor: BLUE,
                px: 3,
                py: 1.1,
                fontWeight: 800,
                boxShadow: "none",
                mb: 1,
                textTransform: "none",
                "&:hover": { backgroundColor: BLUE_DARK, boxShadow: "none" },
              }}
            >
              {t("Book Appointment")}
            </Button>
            </Box>
          </Container>
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: BLUE_LIGHTER,
          color: TEXT_DARK,
          py: { xs: 3.5, md: 4 },
          borderTop: `1px solid ${BORDER}`,
        }}
      >
        <Container maxWidth={false} sx={PAGE_GUTTER}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={5}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "6px",
                    backgroundColor: BLUE,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <LocalHospitalIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: TEXT_DARK }}>HMS</Typography>
              </Box>
              <Typography sx={{ maxWidth: 460, fontSize: 13, lineHeight: 1.7, color: TEXT_GREY }}>
                {t("Hospital Management System - patient portal, clinical records, pharmacy, laboratory and billing in a single suite.")}
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography sx={{ fontWeight: 700, mb: 1.5, color: TEXT_DARK }}>{t("For Patients")}</Typography>
              {["Find a Doctor", "Book Appointment", "OPD", "Pathology"].map((item) => (
                <Typography
                  key={item}
                  onClick={() =>
                    handleNavigation(
                      item === "Find a Doctor" || item === "Book Appointment"
                        ? "doctor-section"
                        : "modules-section"
                    )
                  }
                  sx={{
                    fontSize: 13,
                    color: TEXT_GREY,
                    mb: 0.8,
                    cursor: "pointer",
                    "&:hover": { color: BLUE },
                  }}
                >
                  {t(item)}
                </Typography>
              ))}
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography sx={{ fontWeight: 700, mb: 1.5, color: TEXT_DARK }}>{t("Specialities")}</Typography>
              {["Appointments", "Pharmacy", "Laboratory", "Billing"].map((item) => (
                <Typography
                  key={item}
                  onClick={() => handleNavigation("modules-section")}
                  sx={{
                    fontSize: 13,
                    color: TEXT_GREY,
                    mb: 0.8,
                    cursor: "pointer",
                    "&:hover": { color: BLUE },
                  }}
                >
                  {t(item)}
                </Typography>
              ))}
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography sx={{ fontWeight: 700, mb: 1.5, color: TEXT_DARK }}>{t("Care at HMS")}</Typography>
              <Typography sx={{ fontSize: 13, lineHeight: 1.7, color: TEXT_GREY }}>
                {t("Book specialists, manage visits and keep prescriptions and lab reports in one patient portal.")}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 3, borderColor: BORDER }} />
          <Typography sx={{ fontSize: 12, color: TEXT_GREY, textAlign: "center" }}>
            © {new Date().getFullYear()} HMS - Hospital Management System. {t("All Rights Reserved.")}
          </Typography>
        </Container>
      </Box>

      {showModal && (
        <LoginModal
          onClose={() => setShowModal(false)}
          onLoginSuccess={handleLoginSuccess}
          onOtpVerified={handleOtpVerified}
          language={language}
        />
      )}

{verifiedMobile && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1400,
            overflowY: "auto",
          }}
        >
          <PatientAddProfile
            verifiedMobile={verifiedMobile}
            onBack={() => setVerifiedMobile("")}
            onCompleted={async () => {
              setVerifiedMobile("");
            
              if (pendingBookTest) {
                setPendingBookTest(false);
                navigate("/patient?bookTest=1");
                return;
              }
            
              navigate("/patient");
            }}
          />
        </Box>
      )}
    </Box>
  );
};


export default PatientLoginPage;
export { LoginModal };